import { useEffect, useRef, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { Typography } from '../../../components/typography/typography';
import { Pagination } from '../../../clients/quipli-service/pagination/pagination';
import { DateTime } from 'luxon';
import { Location } from '../../../clients/quipli-service/location/location';
import FileSaver from 'file-saver';
import { QuipliLink } from '../../../components/link/quipli-link';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import { Spinner } from '../../../components/spinner/spinner';
import { Paginator } from '../../../components/paginator/paginator-v2';
import { CustomerSearchesReportRow, RenterReportRequest } from '../../../clients/quipli-service/report/report';
import { useSearchParams } from 'react-router-dom';
import { SearchBar } from '../../../components/search-bar/search-bar';
import classNames from 'classnames';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { SortIcon } from '../../../components/table/desktop-data-table';
import { FiltersModal } from '../../../components/modal/filters-modal';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { Badge } from '../../../components/badge/badge';
import { LocationSelect } from '../../location/select/location-select';
import { toast } from '../../../util/toasts';
import { SpinnerModal } from '../../../components/modal/spinner-modal';

function extractRenterReportSearchParams(params: URLSearchParams): RenterReportRequest {
  return {
    keyword: params.get('keyword') || undefined,
    pageNumber: params.get('pageNumber') ? parseInt(params.get('pageNumber')!) : undefined,
    pageSize: params.get('pageSize') ? parseInt(params.get('pageSize')!) : undefined,
    sortBy: params.get('sortBy') || undefined,
    sortDirection: params.get('sortDirection') || undefined,
    from: params.get('from') ? DateTime.fromISO(params.get('from')!) : undefined,
    to: params.get('to') ? DateTime.fromISO(params.get('to')!) : undefined,
  } as RenterReportRequest;
}

export const CustomerSearchesReport = () => {
  const { exportCustomerSearchesReport, customerSearchesReport } = useReportApiV2();
  const { getMultipleConfigurations } = useAPI();
  const [timezone, setTimezone] = useState<string>();
  const [location, setLocation] = useState<Location | undefined>();
  const [customerSearchesReportResult, setCustomerSearchesReportResult] =
    useState<Pagination<CustomerSearchesReportRow>>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filtersModal, setFiltersModal] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<DateTime | undefined>();
  const [startDate, setStartDate] = useState<DateTime | undefined>();
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const searchRef = useRef<any>(null);

  const loadReport = async () => {
    try {
      setIsLoading(true);
      const searchReport = await customerSearchesReport({ ...reportRequest, locationId: location?.id });
      setCustomerSearchesReportResult(searchReport);
    } catch (error) {
      toast('Failed to load report', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // List searchRequest hooks
  const [params] = useSearchParams();
  const {
    request: reportRequest,
    setRequest: setReportRequest,
    numOfParams,
  } = useRequestQueryParams<RenterReportRequest>(extractRenterReportSearchParams(params), loadReport, [location]);

  // Hooks
  useEffect(() => {
    const init = async () => {
      const confs = await getMultipleConfigurations(['CONF_TIMEZONE']);
      const _timezone = confs.find((c) => c.variable === 'CONF_TIMEZONE')?.value;
      if (_timezone) {
        setTimezone(_timezone);
      }
      setEndDate(params.has('from') ? DateTime.fromISO(params.get('from')!) : undefined);
      setStartDate(params.has('to') ? DateTime.fromISO(params.get('to')!) : undefined);
    };
    init();
  }, []);

  const exportReport = async () => {
    setIsExporting(true);
    try {
      const file = await exportCustomerSearchesReport({
        ...{ ...reportRequest, locationId: location?.id },
        pageNumber: undefined,
        pageSize: undefined,
      });
      const exportFileName = `Users_Report_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';
      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (e) {
      toast('Failed to export report', 'error');
      console.log(e);
    } finally {
      setIsExporting(false);
    }
  };

  function toggleSort() {
    setReportRequest((req) => ({ ...req, sortDirection: req.sortDirection === 'asc' ? 'desc' : 'asc' }));
  }

  function resetSearch() {
    setStartDate(undefined);
    setEndDate(undefined);
    setLocation(undefined);

    if (searchRef.current?.setKeyword) {
      searchRef.current.setKeyword('');
    }

    setReportRequest((req) => {
      return { ...req, keyword: undefined, sortDirection: undefined, from: undefined, to: undefined };
    });
  }

  return (
    <div className="payments-report-v2 flex flex-column gap-4">
      <div className="flex justify-between items-center">
        <Typography type="display-xs" weight="medium" className="text-[var(--grey-800)]">
          Customer Searches Report
        </Typography>
      </div>

      <div className="payments-report-v2 flex w-full flex-wrap justify-between">
        <div className={'flex flex-row gap-2'}>
          <SearchBar
            ref={searchRef}
            data-cy="search-renters"
            placeholder="Search by keyword"
            defaultValue={reportRequest.keyword}
            setRequest={setReportRequest}
          />
          <Button
            className="button-secondary !border-0 h-[46px] relative"
            onClick={() => setFiltersModal(true)}
            data-cy="filter-renters"
          >
            <Typography
              type="text-md"
              className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
            >
              <Icon icon="FilterLines" stroke="var(--grey-700)" /> Filters
            </Typography>
            {numOfParams > 0 && (
              <Badge className="absolute -right-2 -top-2" text={`${numOfParams}`} size="small" type="yellow" />
            )}
          </Button>
          {numOfParams > 0 && (
            <Button
              className="button-secondary !border-0 h-[46px]"
              onClick={() => resetSearch()}
              data-cy="clear-filter-order"
            >
              <Typography
                type="text-md"
                className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
              >
                Clear Filters
              </Typography>
            </Button>
          )}
        </div>
        <Button
          className="self-start lg:self-end	button-yellow !border-0 w-[114px] h-[40px]"
          onClick={() => exportReport()}
          data-cy="export"
        >
          <Typography
            type="text-md"
            weight="semi-bold"
            className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
          >
            Export
          </Typography>
          <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
        </Button>
      </div>
      <div className="w-full md:w-[548px]">
        <LocationSelect restoreLast={false} setLocation={(l) => setLocation(l || undefined)} clearable={true} />
      </div>
      <div className="bg-white rounded-[12px]">
        <div className="data-table overflow-x-auto overflow-y-visible bg-white">
          <table>
            <thead>
              <tr>
                <th className="text-left">Search Term</th>
                <th className="text-left">Location</th>
                <th className="text-left">User</th>
                <th className="text-left">
                  <span
                    className={classNames('flex pr-2 gap-1 whitespace-nowrap', {
                      sortable: 'cursor-pointer',
                    })}
                    onClick={() => toggleSort()}
                  >
                    Search Date
                    <SortIcon currentDirection={reportRequest.sortDirection} />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={100}>
                    <Spinner />
                  </td>
                </tr>
              ) : (
                <>
                  {customerSearchesReportResult && (
                    <>
                      {customerSearchesReportResult.items.length > 0 ? (
                        customerSearchesReportResult.items.map((report, i) => (
                          <TableRow key={i} customerSearch={report} />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={100}>No record found</td>
                        </tr>
                      )}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
        <Paginator
          pagination={customerSearchesReportResult?.pagination}
          request={reportRequest}
          setRequest={(pag) =>
            setReportRequest((req) => {
              return { ...req, ...pag };
            })
          }
        />
        <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Customer Searches'} />
      </div>
      <FiltersModal
        isOpen={filtersModal}
        title="Filters"
        onConfirm={() => {
          setFiltersModal(false);
          setReportRequest((prev) => {
            return { ...prev, ...{ from: startDate, to: endDate } };
          });
        }}
        onCancel={() => {
          setFiltersModal(false);
        }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium" className="text-[var(--grey-700)]">
              Search Range
            </Typography>
            <div className="flex flex-wrap gap-2">
              <DatePicker
                value={startDate}
                onChange={(d) => {
                  setStartDate(d?.setZone(timezone, { keepLocalTime: true }).startOf('day'));
                }}
                clearable={true}
              />
              <DatePicker
                value={endDate}
                onChange={(d) => setEndDate(d?.setZone(timezone, { keepLocalTime: true }).endOf('day'))}
                clearable={true}
              />
            </div>
          </div>
        </div>
      </FiltersModal>
    </div>
  );
};

const TableRow = ({ key, customerSearch: report }: { key: number; customerSearch: CustomerSearchesReportRow }) => {
  return (
    <tr key={key}>
      <td className="text-left">{report['Keyword']}</td>
      <td>
        <QuipliLink
          className="underline underline-offset-1 text-[var(--quipli-black-800)]"
          to={`shops/form/1/${report['locationId']}`}
        >
          {report['Location']}
        </QuipliLink>
      </td>
      <td>
        {report['User'] && (
          <QuipliLink
            className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            to={`users/customer_form/${report['userId']}`}
          >
            {report['User']}
          </QuipliLink>
        )}
      </td>
      <td className="text-left Date">
        <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
          {DateTime.fromISO(report['Search Date']).toFormat('LLL-dd')}
        </Typography>
        <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
          {DateTime.fromISO(report['Search Date']).toFormat('h:mm a')}
        </Typography>
      </td>
    </tr>
  );
};
